/** 지도·결함 리스트 공통 — 결함 유형 아이콘·표시명 */
import enLocales from '@/locales/en.json';

export const DEFECT_TYPE_ICONS_URI = '/img/icons/defect/';

/**
 * 유형 코드 정규화 (조회·비교용).
 * PNG 파일명은 언더스코어(`cell_multi.png`)이므로 하이픈이 아니라 `_` 로 맞춘다.
 */
export function normalizeDefectTypeCode(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')
    .replace(/_+/g, '_');
}

const SEVERITY_SUFFIX = /_(low|medium|high)$/;

/**
 * 파일명과 다른 과거 코드.
 * 예: MULTI_CELL_DEFECT → cell_multi.png, CELL-DEFECT → cell.png
 */
const ICON_BASE_ALIASES = {
  cell_defect: 'cell',
  multi_cell: 'cell_multi',
  multi_cell_defect: 'cell_multi',
  diode_issue: 'diode'
};

/** 표시명은 카탈로그 코드(CELL_MULTI 등)로 맞춘다. 아이콘만 다른 diode_issue 는 제외. */
const CATALOG_BASE_ALIASES = {
  cell_defect: 'CELL',
  multi_cell: 'CELL_MULTI',
  multi_cell_defect: 'CELL_MULTI'
};

function splitSeverity(normalizedKey) {
  const match = String(normalizedKey || '').match(SEVERITY_SUFFIX);
  if (!match) {
    return { base: normalizedKey, severity: '' };
  }
  return {
    base: normalizedKey.slice(0, -match[0].length),
    severity: match[1]
  };
}

function rawDefectTypeValue(item) {
  const fromCodeObj =
    item?.defectCode != null && typeof item.defectCode === 'object'
      ? item.defectCode.code
      : item?.defectCode ?? item?.defect_code;
  const raw =
    (item?.defectType && typeof item.defectType === 'object'
      ? item.defectType.code
      : item?.defectType) ??
    fromCodeObj ??
    item?.code;
  if (raw != null && typeof raw === 'object' && raw.code) {
    return raw.code;
  }
  return raw;
}

/**
 * 카탈로그·locale 조회용 코드. 빈 값은 빈 문자열 (Cell 로 바꾸지 않음).
 * @param {unknown} value
 * @returns {string}
 */
export function toCatalogDefectTypeCode(value) {
  const key = normalizeDefectTypeCode(value);
  if (!key) {
    return '';
  }
  const { base, severity } = splitSeverity(key);
  const catalogBase = CATALOG_BASE_ALIASES[base] || base.toUpperCase();
  return severity ? `${catalogBase}_${severity.toUpperCase()}` : catalogBase;
}

/**
 * @param {{ defectType?: string, defectCode?: { code?: string }|string, defect_code?: string, code?: string }} item
 */
export function resolveDefectTypeIconKey(item) {
  const raw = rawDefectTypeValue(item);
  if (raw == null || raw === '') {
    return 'cell';
  }
  const key = normalizeDefectTypeCode(raw);
  if (!key) {
    return 'cell';
  }
  const { base, severity } = splitSeverity(key);
  const iconBase = ICON_BASE_ALIASES[base] || base;
  return severity ? `${iconBase}_${severity}` : iconBase;
}

export function getDefectTypeIconUrl(item) {
  return `${DEFECT_TYPE_ICONS_URI}${resolveDefectTypeIconKey(item)}.png`;
}

/**
 * @param {string} code
 * @param {Array<{ code?: string, name?: string, children?: Array }>} parentTypes
 */
export function findDefectTypeRecordByCode(code, parentTypes = []) {
  const target = normalizeDefectTypeCode(code);
  if (!target) {
    return null;
  }
  for (const parent of parentTypes) {
    if (normalizeDefectTypeCode(parent?.code) === target) {
      return parent;
    }
    for (const child of parent?.children || []) {
      if (normalizeDefectTypeCode(child?.code) === target) {
        return child;
      }
    }
  }
  return null;
}

/**
 * 결함 유형·상세 유형 명칭은 locale과 관계없이 영어 고유명사로 표시한다.
 * @param {string|null|undefined} code
 * @param {string|null|undefined} [fallback]
 * @returns {string}
 */
export function englishDefectTypeName(code, fallback) {
  const normalized = String(code ?? '')
    .trim()
    .toUpperCase()
    .replace(/-/g, '_');
  const fromCatalog = normalized ? enLocales.defectTypeNames?.[normalized] : '';
  if (fromCatalog) {
    return fromCatalog;
  }
  if (fallback == null || String(fallback).trim() === '') {
    return '';
  }
  return fallback;
}

/**
 * @param {object} item
 * @param {Array} parentTypes
 */
export function resolveDefectTypeDisplayName(item, parentTypes = []) {
  const fromCodeObj =
    item?.defectCode != null && typeof item.defectCode === 'object'
      ? item.defectCode.code
      : item?.defectCode ?? item?.defect_code;
  const raw =
    (item?.defectType && typeof item.defectType === 'object'
      ? item.defectType.code
      : item?.defectType) ??
    fromCodeObj ??
    item?.code;
  const catalogCode = toCatalogDefectTypeCode(raw);
  const fromCatalog = englishDefectTypeName(catalogCode) || englishDefectTypeName(raw);
  if (fromCatalog) {
    return fromCatalog;
  }
  const key = catalogCode ? normalizeDefectTypeCode(catalogCode) : '';
  const record = key ? findDefectTypeRecordByCode(key, parentTypes) : null;
  if (record?.name) {
    return englishDefectTypeName(record.code, record.name);
  }
  if (item?.defectDesc) {
    return item.defectDesc;
  }
  if (item?.defectType && typeof item.defectType === 'string') {
    return item.defectType;
  }
  return '';
}
